<template>
	<div>
		<SetupTabs
			:panels="panels"
			v-on="$listeners"
			ref="setup_panel"
			:installationMode="installationMode"
			:openedPanelIndex="openedPanelIndex"
			:didUserTriedToSaveOnce="didUserTriedToSaveOnce"
			:notValidConfigurationsIds="notValidConfigurationsIds"
		/>
		<template v-if="!editMode">
			<v-expand-transition v-for="(panel, i) in panels" :key="i">
				<EditConfigurationMenu
					v-show="i === openedPanelIndex"
					:key="i"
					class="px-5"
					:ref="panelRef(panel)"
					v-on="$listeners"
					:editMode="editMode"
					:widget="panel.widget"
					:configurationId="panel.configurationId"
					:isGeneralConfiguration="panel.isGeneralConfiguration"
				/>
			</v-expand-transition>
		</template>
		<EditConfigurationMenu
			v-else
			editMode
			class="px-5"
			v-on="$listeners"
			:ref="openedPanel.ref"
			:widget="openedPanel.widget"
			:configurationId="openedPanel.configurationId"
			:isGeneralConfiguration="openedPanel.isGeneralConfiguration"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { initWidgetFields } from '@/store/modules/widgetEditor/actions';
import SetupTabs from '@/views/Flow/configurations/SetupMenu/SetupTabs';
import { editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import EditConfigurationMenu from '@/views/Flow/configurations/SetupMenu/EditConfigurationMenu';
import { getConfiguration, getConfigurationResponse } from '@/store/modules/workflowConfigurations/getters';
import { getConfiguration as getConfigurationAction } from '@/store/modules/workflowConfigurations/actions';
import { widget as userConfWidget } from '@/store/modules/configurationEditor/modules/userConfigurationWidgetEditor/getters';
import { widget as generalConfWidget } from '@/store/modules/configurationEditor/modules/generalConfigurationWidgetEditor/getters';
import { initWidgetFields as initWidgetFieldsUser } from '@/store/modules/configurationEditor/modules/userConfigurationWidgetEditor/actions';
import { initWidgetFields as initWidgetFieldsGeneral } from '@/store/modules/configurationEditor/modules/generalConfigurationWidgetEditor/actions';

export default {
	name: 'SetupMenuContent',
	props: {
		panels: { type: Array },
		editMode: { type: Boolean },
		openedPanelIndex: { type: Number },
		configurationIds: { type: Object },
		installationMode: { type: Boolean },
		userConfInterface: { type: Object },
		generalConfInterface: { type: Object },
		generalConfigurationId: { type: Number },
		didUserTriedToSaveOnce: { type: Boolean },
		notValidConfigurationsIds: { type: Array },
		userSpecificConfigurationId: { type: Number },
	},
	components: {
		SetupTabs,
		EditConfigurationMenu,
	},
	computed: {
		...mapGetters([getConfiguration.fullPath, getConfigurationResponse.fullPath]),
		...mapGetters([editedWorkflowVersion.fullPath, userConfWidget.fullPath, generalConfWidget.fullPath]),
		openedPanel() {
			return this.panels[this.openedPanelIndex];
		},
		openedPanelInterfaceId() {
			const fullConfiguration = this.getFullConfiguration(this.openedPanel?.configurationId);
			const allConfigurationInterfaces = this.getConfigurationInterfaces(fullConfiguration);
			return allConfigurationInterfaces[0]?.interface?.id;
		},
	},
	methods: {
		...mapActions([initWidgetFields.fullPath, getConfigurationAction.fullPath]),
		...mapActions([initWidgetFieldsGeneral.fullPath, initWidgetFieldsUser.fullPath]),
		panelRef(panel) {
			return panel?.ref;
		},
		getFullConfiguration(configurationId) {
			return this[getConfiguration.fullPath](configurationId);
		},
		getConfigurationInterfaces(configuration) {
			return configuration?.workflow_configuration_interfaces || [];
		},
		async addNewBlock(configurationType) {
			// Triggered from parent
			this.$emit('tab-clicked', configurationType === 'general' ? 0 : 1);
			this.$nextTick(() => {
				const openedPanelRef = this.$refs?.[this.openedPanel?.ref];
				openedPanelRef.addNewBlock();
			});
		},
		async getConfigurationsData() {
			if (this.generalConfigurationId) {
				await this[getConfigurationAction.fullPath]({ id: this.generalConfigurationId, forceGet: true });
			}
			if (this.userSpecificConfigurationId) {
				await this[getConfigurationAction.fullPath]({ id: this.userSpecificConfigurationId, forceGet: true });
			}
		},
		initWidgetsFields(payload, isGeneralConfiguration) {
			const action = isGeneralConfiguration ? initWidgetFieldsGeneral : initWidgetFieldsUser;
			this[action.fullPath](payload);
		},
		initializeWidgetsFields() {
			this.panels.forEach((panel) => {
				const fullConfiguration = this.getFullConfiguration(panel.configurationId);
				const allConfigurationInterfaces = this.getConfigurationInterfaces(fullConfiguration);
				const interfaceId = allConfigurationInterfaces[0]?.interface?.id;
				if (interfaceId) {
					this.initWidgetsFields(
						{ id: interfaceId, workflowVersionId: this[editedWorkflowVersion.fullPath].id },
						panel.isGeneralConfiguration
					);
				}
			});
		},
		async initializeWidgets() {
			await this.getConfigurationsData(); // I suspect this await slows the animation of the expansion-panel.
			this.initializeWidgetsFields();
		},
	},

	async mounted() {
		if (!this.generalConfigurationId && !this.userSpecificConfigurationId) {
			return this.$emit('edit-structure');
		}
		this.initializeWidgets();
	},
	watch: {
		openedPanelInterfaceId: {
			handler(newV) {
				if (newV) {
					if (this.editMode) {
						this[initWidgetFields.fullPath]({ id: newV, workflowVersionId: this[editedWorkflowVersion.fullPath].id });
						this.$emit('general-configuration-mode-changed', this.openedPanel.isGeneralConfiguration);
					}
				}
			},
			immediate: true,
		},
		editMode: {
			handler() {
				this.initializeWidgets();
			},
		},
	},
};
</script>

<style scoped lang="scss"></style>
